import React, { SetStateAction, useEffect, useState } from "react";
import {
  ButtonBase,
  ThemeProvider,
  theme,
  BetaForm as useBetaForm,
  TextInputBase,
  CheckboxBase,
  ModalBase,
} from "@reusejs/react";
import { XIcon } from "@heroicons/react/solid";
import { StepperComponent } from "./StepperComponent";
import { useRef } from "react";
import { FileUploaderBase, LabelBase } from "@reusejs/react";
import { uploadFile, convertAmmountToWords } from "services";
import { v4 as uuidv4 } from "uuid";
import ConfirmationModal from "./confirmationModal";

const newTheme = {
  ...theme,
  variants: {},
};

interface PrefillData {
  email?: string;
  mobile_number?: string;
  name?: string;
  email1?: string;
  pan?: string;
  kyc_verified?: boolean;
  emd_amount_paid?: boolean;
  agreement_signed?: boolean;
  application_form_filled?: boolean;
  initial_bid_price?: number | string;
  meta?: {
    user_details?: {
      mobile_number?: string;
    };
    name?: string;
    pan?: string;
    initial_bid_price?: number | string;
  };
  jointBiddersCreate?: boolean;
}

type PrefillDataKey = keyof PrefillData;

interface AddBidderModalProps {
  biddersCount?: number;
  prefillData?: PrefillData;
  excludeEmails?: string[];
  onAction: (action: string) => void;
  color?: string;
  router?: any;
  heading?: string;
  description?: string;
  type?: "create" | "edit";
  callback: (form: any, value: any) => Promise<any>;
  jointBiddersCreate?: boolean;
  activeStep?: number;
  setActiveStep?: React.Dispatch<SetStateAction<number>>;
  allBiddersData?: any;
  setAllBiddersData?: React.Dispatch<SetStateAction<Array<any>>>;
  primaryBidderIndex?: number;
  setPrimaryBidderIndex?: React.Dispatch<SetStateAction<number>>;
  handleNextStep?: (data: any) => void;
  handlePreviousStep?: () => void;
  handleSubmitAllBidders?: ({ selectedFiles, randomID }: any) => void;
}

export default function AddBidderModal({
  biddersCount,
  activeStep,
  setActiveStep = () => {},
  allBiddersData,
  setAllBiddersData,
  primaryBidderIndex,
  setPrimaryBidderIndex,
  handleNextStep,
  handlePreviousStep,
  jointBiddersCreate,
  handleSubmitAllBidders,
  ...props
}: AddBidderModalProps) {
  const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;

  useEffect(() => {
    if (props.prefillData && jointBiddersCreate) {
      form.setField("email", props?.prefillData?.email1 ?? "");
      form.setField("mobile_number", props?.prefillData?.mobile_number ?? "");
      form.setField("name", props?.prefillData?.name ?? "");
      form.setField("pan", props?.prefillData?.pan ?? "");
      form.setField("kyc_verified", props?.prefillData?.kyc_verified ?? false);
      form.setField(
        "emd_amount_paid",
        props?.prefillData?.emd_amount_paid ?? false,
      );
      form.setField(
        "agreement_signed",
        props?.prefillData?.agreement_signed ?? false,
      );
      form.setField(
        "application_form_filled",
        props?.prefillData?.application_form_filled ?? false,
      );
    }
  }, [props.prefillData]);

  const [randomID, setRandomID] = useState<any>();
  const [afterUploadedFiles, setAfterUploadedFiles] = React.useState<any>([]);
  const [selectedFiles, setSelectedFiles] = React.useState<any>([]);

  const openConfirmationModal = async (
    title: string,
    message: string,
    color: string,
    onConfirm: () => Promise<void>,
  ) => {
    const ConfirmationModalWrapper = (modalProps: any) => {
      return (
        <ConfirmationModal
          heading={title}
          description={message}
          callback={onConfirm}
          color={color}
          onAction={modalProps.onAction}
        />
      );
    };

    await ModalBase(
      {
        content: ConfirmationModalWrapper,
        timeout: 0,
        backgroundColor: "bg-black/30",
        backgroundOpacity: "opacity-70",
        modalBaseClasses: {
          background: "bg-white",
          border: "border-2 rounded-lg",
          alignment: "text-left overflow-visible",
          padding: "p-0",
          small: "sm:my-8 sm:w-full sm:max-w-md",
        },
      },
      1000,
      "__next",
    );
  };
  const fileUploadRef = useRef<any>(null);

  const form = useBetaForm({
    email: props.prefillData ? props.prefillData.email : "",
    mobile_number: props.prefillData
      ? props.prefillData?.meta?.user_details?.mobile_number
      : "",
    name: props.prefillData
      ? props.prefillData?.meta?.name
        ? props.prefillData?.meta?.name
        : ""
      : "",
    pan: props.prefillData
      ? props.prefillData?.meta?.pan
        ? props.prefillData?.meta?.pan
        : ""
      : "",
    kyc_verified: props.prefillData ? props.prefillData.kyc_verified : false,
    emd_amount_paid: props.prefillData
      ? props.prefillData.emd_amount_paid
      : false,
    agreement_signed: props.prefillData
      ? props.prefillData.agreement_signed
      : false,
    application_form_filled: props.prefillData
      ? props.prefillData.application_form_filled
      : false,
    initial_bid_price: props.prefillData
      ? props.prefillData.initial_bid_price ||
        props.prefillData?.meta?.initial_bid_price ||
        ""
      : "",
  });

  const form_constraints = {
    email: {
      presence: {
        allowEmpty: false,
      },
      email: true,
      exclusion: props.excludeEmails,
    },
    mobile_number: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: mobileNumberRegEx,
        message: "^Mobile number is not valid.",
      },
    },
    name: {
      presence: {
        allowEmpty: false,
      },
    },
    pan: {
      presence: {
        allowEmpty: false,
      },
    },
  };

  useEffect(() => {
    form.setValidationRules(form_constraints);
    setRandomID(uuidv4());
  }, []);

  return (
    <ThemeProvider value={newTheme}>
      <div className="relative shadow-sm flex flex-col">
        <div className="flex-1">
          <div
            className={
              "absolute z-50 -top-4 -right-4 rounded-full bg-gray-50 items-center px-2 py-2 border-b text-xs text-gray-900"
            }
          >
            <div
              className="cursor-pointer"
              onClick={() => props.onAction("no")}
            >
              <XIcon className="h-4 w-4" aria-hidden="true" />
            </div>
          </div>
          <div className={"relative border-b " + props.color}>
            <div className="px-4 lg:px-8">
              <div className="relative py-6 text-center">
                <h2 className="text-xl font-semibold tracking-tight">
                  {props.heading}
                </h2>
              </div>
              {jointBiddersCreate && (
                <StepperComponent
                  activeStep={activeStep ?? 0}
                  setActiveStep={setActiveStep}
                  totalSteps={
                    Number(biddersCount) ? Number(biddersCount) + 1 : 0
                  }
                />
              )}
            </div>
          </div>
          <div className="p-4 w-2xl">
            <div className="mt-2 min-h-max text-center text-base text-gray-500">
              {props.description}
            </div>
            {jointBiddersCreate && activeStep === Number(biddersCount) ? (
              <div className="mt-2 space-y-4 w-full">
                <div className="bg-gray-50 py-2 px-2 border">
                  <JointBidderUploader
                    selectedFiles={selectedFiles}
                    setSelectedFiles={setSelectedFiles}
                    afterUploadedFiles={afterUploadedFiles}
                    setAfterUploadedFiles={setAfterUploadedFiles}
                    randomID={randomID}
                    fileUploadRef={fileUploadRef}
                  />
                </div>
              </div>
            ) : (
              <div className="mt-2 space-y-4 w-full">
                <div>
                  <TextInputBase
                    labelBaseProps={{
                      label: "Email*",
                    }}
                    name={"email"}
                    onChange={(val) => form.setField("email", val)}
                    placeholder={"Enter bidder's email"}
                    type={"text"}
                    error={
                      form.errors.get("email") ? (
                        <p className="mt-2 text-sm text-red-500">
                          {form.errors.get("email")[0]}
                        </p>
                      ) : null
                    }
                    value={form.getField("email")}
                    htmlFor={"email"}
                    disabled={props.type === "edit"}
                  />
                </div>
                <div>
                  <TextInputBase
                    labelBaseProps={{
                      label: "Mobile No.*",
                    }}
                    name={"mobile_number"}
                    onChange={(val) => form.setField("mobile_number", val)}
                    placeholder={"Enter bidder's mobile no."}
                    type={"text"}
                    error={
                      form.errors.get("mobile_number") ? (
                        <p className="mt-2 text-sm text-red-500">
                          {form.errors.get("mobile_number")[0]}
                        </p>
                      ) : null
                    }
                    value={form.getField("mobile_number")}
                    htmlFor={"mobile_number"}
                    disabled={props.type === "edit"}
                  />
                </div>
                <div>
                  <TextInputBase
                    labelBaseProps={{
                      label: "Name*",
                    }}
                    name={"name"}
                    onChange={(val) => form.setField("name", val)}
                    placeholder={"Enter bidder's name"}
                    type={"text"}
                    error={
                      form.errors.get("name") ? (
                        <p className="mt-2 text-sm text-red-500">
                          {form.errors.get("name")[0]}
                        </p>
                      ) : null
                    }
                    value={form.getField("name")}
                    htmlFor={"name"}
                  />
                </div>
                <div>
                  <TextInputBase
                    labelBaseProps={{
                      label: "PAN Number*",
                    }}
                    name={"pan"}
                    onChange={(val) => form.setField("pan", val)}
                    placeholder={"Enter bidder's pan number"}
                    type={"text"}
                    error={
                      form.errors.get("pan") ? (
                        <p className="mt-2 text-sm text-red-500">
                          {form.errors.get("pan")[0]}
                        </p>
                      ) : null
                    }
                    value={form.getField("pan")}
                    htmlFor={"pan"}
                  />
                </div>
                <div>
                  <TextInputBase
                    labelBaseProps={{
                      label: "Initial Bid Price",
                    }}
                    name={"initial_bid_price"}
                    onChange={(val) => {
                      const numValue = Number(val);
                      if (val === "" || val === null || val === undefined) {
                        form.setField("initial_bid_price", "");
                      } else if (!isNaN(numValue) && numValue >= 0) {
                        form.setField("initial_bid_price", numValue);
                      }
                    }}
                    placeholder={"Enter Initial Bid Price"}
                    type={"number"}
                    min={"0"}
                    error={
                      form.errors.get("initial_bid_price") ? (
                        <p className="mt-2 text-sm text-red-500">
                          {form.errors.get("initial_bid_price")[0]}
                        </p>
                      ) : null
                    }
                    value={form.getField("initial_bid_price")}
                    htmlFor={"initial_bid_price"}
                  />
                  {form.getField("initial_bid_price") && (
                    <p className="mt-2 text-sm italic">
                      <span className="font-semibold">Price in words: </span>
                      <span className="text-[#4DBB3E] font-semibold uppercase">
                        {convertAmmountToWords(
                          Number(form.getField("initial_bid_price")),
                        ) || "Invalid amount"}
                      </span>
                    </p>
                  )}
                </div>
                <div
                  className={` ${
                    jointBiddersCreate
                      ? "grid grid-cols-2 gap-y-4"
                      : "space-y-4"
                  }`}
                >
                  <div>
                    <CheckboxBase
                      labelBaseProps={{
                        label: "KYC verified",
                      }}
                      htmlFor="kyc_verified"
                      name="kyc_verified"
                      type="text"
                      value={form.getField("kyc_verified")}
                      onChange={() =>
                        form.setField(
                          "kyc_verified",
                          !form.getField("kyc_verified"),
                        )
                      }
                      checkboxBaseClasses={{
                        textColor: "text-blue-700",
                      }}
                    />
                  </div>
                  <div>
                    <CheckboxBase
                      labelBaseProps={{
                        label: "EMD Amount Paid",
                      }}
                      htmlFor="emd_amount_paid"
                      name="emd_amount_paid"
                      type="text"
                      value={form.getField("emd_amount_paid")}
                      onChange={() =>
                        form.setField(
                          "emd_amount_paid",
                          !form.getField("emd_amount_paid"),
                        )
                      }
                      checkboxBaseClasses={{
                        textColor: "text-blue-700",
                      }}
                    />
                  </div>
                  <div>
                    <CheckboxBase
                      labelBaseProps={{
                        label: "Agreement Signed",
                      }}
                      htmlFor="agreement_signed"
                      name="agreement_signed"
                      type="text"
                      value={form.getField("agreement_signed")}
                      onChange={() =>
                        form.setField(
                          "agreement_signed",
                          !form.getField("agreement_signed"),
                        )
                      }
                      checkboxBaseClasses={{
                        textColor: "text-blue-700",
                      }}
                    />
                  </div>
                  <div>
                    <CheckboxBase
                      labelBaseProps={{
                        label: "Application Form Filled",
                      }}
                      htmlFor="application_form_filled"
                      name="application_form_filled"
                      type="text"
                      value={form.getField("application_form_filled")}
                      onChange={() =>
                        form.setField(
                          "application_form_filled",
                          !form.getField("application_form_filled"),
                        )
                      }
                      checkboxBaseClasses={{
                        textColor: "text-blue-700",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {jointBiddersCreate ? (
            <div className="flex justify-center items-center space-x-4 py-4 border-t">
              <ButtonBase
                variant="primary"
                disabled={activeStep === 0}
                type="button"
                label={"Back"}
                onClick={async () => {
                  if (handlePreviousStep) handlePreviousStep();
                }}
                buttonBaseClasses={{
                  font: "text-sm font-medium",
                  backgroundColor: "bg-blue-600 hover:bg-blue-700",
                  textColor: "text-white",
                  border: "any",
                  padding: "px-8 py-3",
                }}
              />
              <ButtonBase
                variant="primary"
                type="button"
                disabled={
                  activeStep ===
                  (Number(biddersCount) ? Number(biddersCount) + 1 : 0)
                }
                label={
                  activeStep ===
                  (Number(biddersCount) ? Number(biddersCount) : 0)
                    ? "Submit"
                    : "Next"
                }
                onClick={async () => {
                  let totalStepsCount = Number(biddersCount)
                    ? Number(biddersCount)
                    : 0;

                  if (
                    handleNextStep &&
                    (activeStep ? activeStep : 0) < totalStepsCount
                  )
                    handleNextStep(form);
                  else if (
                    activeStep === totalStepsCount &&
                    handleSubmitAllBidders
                  ) {
                    handleSubmitAllBidders({ selectedFiles, randomID });
                  }
                }}
                buttonBaseClasses={{
                  font: "text-sm font-medium",
                  backgroundColor: "bg-blue-600 hover:bg-blue-700",
                  textColor: "text-white",
                  border: "any",
                  padding: "px-8 py-3",
                }}
              />
            </div>
          ) : (
            <div className="flex justify-center items-center py-4 border-t">
              <ButtonBase
                variant="primary"
                type="button"
                label={props.type === "create" ? "Add Bidder" : "Update Bidder"}
                onClick={async () => {
                  form.value.email = form.value.email.trim().toLowerCase();
                  if (form.validate()) {
                    const initialBidPrice = form.getField("initial_bid_price");

                    if (initialBidPrice && Number(initialBidPrice) > 0) {
                      const priceInWords = convertAmmountToWords(
                        Number(initialBidPrice),
                      );
                      const formattedPrice =
                        Number(initialBidPrice).toLocaleString("en-IN");
                      await openConfirmationModal(
                        "Confirm Initial Bid Price",
                        `You are setting an initial bid price of <strong class="font-bold text-[#4DBB3E]">₹${formattedPrice}</strong> (<strong class="font-bold text-[#4DBB3E] uppercase">${priceInWords}</strong>). Are you sure you want to proceed?`,
                        "bg-blue-50 text-blue-500",
                        async () => {
                          const data = await props.callback(form, form.value);
                          if (data) {
                            props.onAction("no");
                          }
                        },
                      );
                    } else {
                      const data = await props.callback(form, form.value);
                      if (data) {
                        props.onAction("no");
                      }
                    }
                  }
                }}
                buttonBaseClasses={{
                  font: "text-sm font-medium",
                  backgroundColor: "bg-blue-600 hover:bg-blue-700",
                  textColor: "text-white",
                  border: "any",
                  padding: "px-8 py-3",
                }}
              />
            </div>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}

function JointBidderUploader({
  randomID,
  selectedFiles,
  setSelectedFiles,
  afterUploadedFiles,
  setAfterUploadedFiles,
  fileUploadRef,
}: any) {
  const OpenFileUploader = (props: any) => {
    return (
      <div className="mt-2 grid grid-cols-1 grid-flow-col gap-4">
        <div className="bg-gray-50 py-2 px-2 border w-full">
          <div className="flex justify-between items-center border-b pb-4">
            <LabelBase label={"Upload Documents"} />
            <ButtonBase
              type="button"
              label="Select Files"
              onClick={() => {
                props.triggerClickHandler();
              }}
            />
          </div>

          <div className="mt-4">
            {props.selectedFiles.length > 0 && (
              <div className="text-sm text-gray-700 space-y-1">
                {props.selectedFiles.map((file: any, index: number) => {
                  return <div key={index}>{file.file.name}</div>;
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-1">
      <FileUploaderBase
        ref={fileUploadRef}
        multiple={true}
        autoUpload={true}
        selectedFiles={(files) => {
          setSelectedFiles(files);
        }}
        upload={async (selectedFile: File) => {
          console.log("function called", selectedFile);

          const owner_identifier = randomID;
          return await uploadFile(selectedFile, {
            owner_service: "joint-declaration-documents",
            owner_identifier,
          });
        }}
        beforeUpload={() => {
          console.log("inside");
        }}
        afterUpload={(uploadedFiles: any[]) => {
          // setSelectedFiles([]);
          // setAfterUploadedFiles(uploadedFiles);
        }}
        openFileUploader={(triggerClickHandler: any) => (
          <OpenFileUploader
            selectedFiles={selectedFiles}
            afterUploadedFiles={afterUploadedFiles}
            triggerClickHandler={triggerClickHandler}
            fileUploadRef={fileUploadRef}
          />
        )}
      />
    </div>
  );
}
